import React, { useEffect, useMemo, useState } from "react";
import { useRouter } from "next/router";
import { AiOutlineWarning } from "react-icons/ai";
import { Alert, Button, Input, Spinner } from "reactstrap";
import moment from "moment";

import {
  getTrackingDetails,
  mapTrackingError,
} from "@/services/track-shipment";
import {
  mapPublicTracking,
  PublicTrackingViewModel,
  toV2CarrierCode,
} from "@/mappers/tracking/v2PublicTrackingMapper";
import { listOfCarriers } from "@/utils/constants";
import useGoogleAnalytics from "@/hooks/useGoogleAnalytics";
import classes from "@/styles/track-shipment/track-shipment.module.css";

interface TrackShipmentMainProps {
  trackingId?: string;
  carrier?: string;
}

// Distinct UI states. `idle` is the initial form; the others reflect the v2 outcome (real HTTP
// statuses now — the v1 `status:false`-on-200 trap is gone).
type ViewState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "found"; vm: PublicTrackingViewModel }
  | { kind: "not-found" }
  | { kind: "error"; message: string };

// The ShipVista default mark shown when there is no tenant logo (unbranded) or the presigned logo
// URL has expired / failed to load (degrades to default, never a broken image).
const DEFAULT_MARK = "/logoShipvista.png";

const TrackShipmentMain: React.FC<TrackShipmentMainProps> = ({
  trackingId,
  carrier,
}) => {
  const router = useRouter();

  const [shippingCarrier, setShippingCarrier] = useState<string>(carrier || "");
  const [trackingNumber, setTrackingNumber] = useState<string>(
    trackingId || ""
  );
  const [carrierError, setCarrierError] = useState<string>("");
  const [logoFailed, setLogoFailed] = useState<boolean>(false);
  const [view, setView] = useState<ViewState>({ kind: "idle" });
  const [autoSubmitted, setAutoSubmitted] = useState<boolean>(false);

  const { sendCustomEvent } = useGoogleAnalytics();

  // Seed the form from props (the page reads ?tracking_number= / ?carrier= via useRouter). Under
  // static export the query is only available after hydration, so the parent only passes these once
  // the router is ready — but guard here too in case the props arrive late.
  useEffect(() => {
    if (trackingId) setTrackingNumber(trackingId);
  }, [trackingId]);

  useEffect(() => {
    if (carrier) setShippingCarrier(carrier);
  }, [carrier]);

  // Auto-submit a deep link ONCE the router is ready AND both params are present. Under static
  // export `router.query` is empty until hydration — guarding on `router.isReady` prevents firing a
  // request with an empty tracking number / unmapped carrier on first paint.
  useEffect(() => {
    if (!router.isReady || autoSubmitted) return;
    const tn = router.query.tracking_number;
    const c = router.query.carrier;
    const tnStr = Array.isArray(tn) ? tn[0] : tn;
    const cStr = Array.isArray(c) ? c[0] : c;
    if (tnStr && cStr) {
      setTrackingNumber(tnStr);
      setShippingCarrier(cStr);
      setAutoSubmitted(true);
      void handleSubmit(tnStr, cStr);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady, router.query.tracking_number, router.query.carrier]);

  const handleSubmit = async (
    tn: string = trackingNumber,
    uiCarrier: string = shippingCarrier
  ) => {
    const v2Carrier = toV2CarrierCode(uiCarrier);

    // Client-side guards (the backend would 400/404 these, but failing closed here is faster and
    // avoids a guaranteed-silent-404 for an unmapped carrier).
    if (!tn.trim()) {
      setCarrierError("");
      setView({
        kind: "error",
        message: "Please enter a tracking number.",
      });
      return;
    }
    if (!v2Carrier) {
      setCarrierError("Please choose a supported carrier.");
      setView({ kind: "idle" });
      return;
    }
    setCarrierError("");
    setLogoFailed(false);
    setView({ kind: "loading" });

    sendCustomEvent("trackShipment", {
      page: {
        url:
          document.location.origin +
          document.location.pathname +
          document.location.search,
      },
      params: { trackingNumber: tn, shippingCarrier: uiCarrier },
    });

    try {
      const { data } = await getTrackingDetails(tn.trim(), v2Carrier);
      setView({ kind: "found", vm: mapPublicTracking(data) });
    } catch (error) {
      const { code, message } = mapTrackingError(error);
      if (code === "carrier-required") {
        // 400 carrier-required → inline field error on the selector, not a toast.
        setCarrierError(message);
        setView({ kind: "idle" });
      } else if (code === "tracking-not-found") {
        // 404 → the honest not-found state (a buyer-facing page, not an error toast). The v2 route
        // tracks only ShipVista-shipped parcels with projected events; say so plainly.
        setView({ kind: "not-found" });
      } else {
        setView({ kind: "error", message });
      }
    }
  };

  // Branded chrome: applied as inline CSS custom properties ONLY when isBranded (validated hex only).
  // Unbranded / over-quota tenants fall through to the default page — no custom properties set.
  const brandedStyle = useMemo<React.CSSProperties>(() => {
    const vm = view.kind === "found" ? view.vm : null;
    if (!vm || !vm.branding.isBranded) return {};
    const style: Record<string, string> = {};
    if (vm.branding.primaryColorHex)
      style["--tracking-primary"] = vm.branding.primaryColorHex;
    if (vm.branding.accentColorHex)
      style["--tracking-accent"] = vm.branding.accentColorHex;
    return style as React.CSSProperties;
  }, [view]);

  return (
    <main className={classes.container}>
      <section className={classes.hero}>
        <div className={`container`}>
          <h2 className="h2 text-secondary">Track My Shipment</h2>
        </div>
      </section>
      <section>
        <div className={`container my-5`} style={brandedStyle}>
          {/* Branded chrome — only when isBranded. The presigned logoUrl expires (~1 h); onError
              degrades to the default mark so the page never shows a broken image. */}
          {view.kind === "found" && view.vm.branding.isBranded && (
            <div className={classes.chrome}>
              {/* Deliberately <img>, not next/image: next.config.js sets images.unoptimized:true
                  (so next/image is a plain <img> anyway), and the src is a per-response presigned
                  URL that must swap to the default mark via onError when it expires/breaks. */}
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={logoFailed ? DEFAULT_MARK : view.vm.branding.logoUrl}
                alt={view.vm.branding.displayName}
                className={classes.chromeLogo}
                width={48}
                height={48}
                onError={() => setLogoFailed(true)}
              />
              <div>
                <div className={classes.chromeName}>
                  {view.vm.branding.displayName}
                </div>
                <div className={classes.chromeSupport}>
                  {view.vm.branding.supportEmail && (
                    <a href={`mailto:${view.vm.branding.supportEmail}`}>
                      {view.vm.branding.supportEmail}
                    </a>
                  )}
                  {view.vm.branding.supportEmail &&
                    view.vm.branding.supportUrl && <span> · </span>}
                  {view.vm.branding.supportUrl && (
                    <a
                      href={view.vm.branding.supportUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Support
                    </a>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="d-flex gap-3 mb-1 flex-wrap">
            {listOfCarriers.map(({ label, value }) => (
              <div key={value}>
                <Input
                  id={value}
                  type="radio"
                  name="carrier"
                  value={value}
                  checked={shippingCarrier === value}
                  onChange={() => {
                    setShippingCarrier(value);
                    setCarrierError("");
                  }}
                />
                <label htmlFor={value}>&nbsp;{label}</label>
              </div>
            ))}
          </div>
          {carrierError && (
            <div className={classes.fieldError}>{carrierError}</div>
          )}
          <div className="mb-3 mt-3">
            <label htmlFor="number">Tracking Number</label>
            <Input
              value={trackingNumber}
              onChange={({ target }) => setTrackingNumber(target.value)}
              id="number"
              onKeyDown={(e) => {
                if (e.key === "Enter") void handleSubmit();
              }}
            />
          </div>

          <Button
            color="primary"
            variant="contained"
            disabled={view.kind === "loading"}
            onClick={() => void handleSubmit()}
            className="btn-green d-block mx-auto btn-lg"
          >
            Submit{" "}
            {view.kind === "loading" && (
              <span style={{ marginLeft: "10px" }}>
                <Spinner size="sm" />
              </span>
            )}
          </Button>

          {/* Outcomes — the read-only concatenated <textarea> blob from v1 is gone. */}
          {view.kind === "loading" && (
            <div className="mt-4">
              {/* Skeleton keeps the layout reserved while branding hydrates (static export: branding
                  is never in the initial HTML) so the default→branded transition is a fill, not a jump. */}
              <span className={classes.skeleton}>loading</span>
              <div className={`${classes.timeline} mt-3`} aria-hidden="true">
                {[0, 1, 2].map((i) => (
                  <div className={classes.event} key={i}>
                    <div className={classes.eventDateTime}>
                      <span className={classes.skeleton}>looking up…</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {view.kind === "found" && <FoundView vm={view.vm} />}

          {view.kind === "not-found" && (
            <div className={`${classes.notice} mt-4`}>
              <div className={classes.noticeTitle}>
                We couldn&apos;t find that shipment
              </div>
              <div>
                ShipVista tracks shipments created on ShipVista. Double-check
                the carrier and tracking number and try again. If this parcel
                wasn&apos;t shipped through ShipVista, it won&apos;t appear
                here.
              </div>
            </div>
          )}

          {view.kind === "error" && (
            <Alert color="warning" className="mt-4 d-flex align-items-center">
              <AiOutlineWarning className="me-2" />
              <span>{view.message}</span>
            </Alert>
          )}
        </div>
      </section>
    </main>
  );
};

/** The timeline + current-status render for a found shipment. Split out to keep the main render
 *  readable; receives the already-mapped view model (nullable fields already collapsed to "—"). */
const FoundView: React.FC<{ vm: PublicTrackingViewModel }> = ({ vm }) => (
  <div className="mt-4">
    <div className={classes.statusRow}>
      <span
        className={`${classes.statusBadge} ${
          vm.isException ? classes.statusException : ""
        }`}
      >
        {vm.isException && <AiOutlineWarning />}
        {vm.currentStatusLabel}
      </span>
      <span className={classes.trackingRef}>
        {vm.carrierCode} · {vm.trackingNumber}
      </span>
    </div>
    {vm.isException && (
      <div className={`${classes.exceptionNote} mb-3`}>
        This shipment has an exception. Contact the merchant for help.
      </div>
    )}

    {vm.events.length === 0 ? (
      <div className={classes.notice}>
        No tracking events have been published for this shipment yet. Check back
        shortly.
      </div>
    ) : (
      <ul className={classes.timeline}>
        {vm.events.map((e, idx) => (
          <li className={classes.event} key={`${e.occurredAt}-${idx}`}>
            <div className={classes.eventDateTime}>
              {e.occurredAt ? (
                moment(e.occurredAt).format("MMM D, YYYY · h:mm A")
              ) : (
                <span className={classes.eventDash}>—</span>
              )}
            </div>
            {e.location !== "—" && (
              <div className={classes.eventLocation}>{e.location}</div>
            )}
            <div className={classes.eventDescription}>{e.description}</div>
          </li>
        ))}
      </ul>
    )}
  </div>
);

export default TrackShipmentMain;
